import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  IndianRupee,
  Percent,
  Calendar,
  Users,
  Search,
  Filter,
  Download,
  Eye,
  History,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Bell,
  ArrowUpDown,
  Plus,
} from "lucide-react";

const COMMISSION_TYPES = [
  { id: "all", name: "All Types" },
  { id: "percentage", name: "Percentage", icon: Percent },
  { id: "flat_per_ride", name: "Flat per Ride", icon: IndianRupee },
  { id: "fixed_daily", name: "Fixed Daily", icon: Calendar },
  { id: "fixed_weekly", name: "Fixed Weekly", icon: Calendar },
  { id: "fixed_monthly", name: "Fixed Monthly", icon: Calendar },
];

const PAYMENT_METHODS = [
  { id: "cash", name: "Cash" },
  { id: "upi", name: "UPI" },
  { id: "bank_transfer", name: "Bank Transfer" },
  { id: "wallet", name: "Wallet" },
  { id: "other", name: "Other" },
];

export const CommissionReportDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [cities, setCities] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [expiredSubscriptions, setExpiredSubscriptions] = useState(null);
  
  // Separated reports state
  const [perRideReport, setPerRideReport] = useState(null);
  const [subscriptionReport, setSubscriptionReport] = useState(null);
  const [reportView, setReportView] = useState("combined"); // "combined", "per-ride", "subscription"
  
  // Filters
  const [cityFilter, setCityFilter] = useState("all");
  const [commissionTypeFilter, setCommissionTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [hasDuesFilter, setHasDuesFilter] = useState("yes");  // Default to "With Dues"
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'amount_owed', direction: 'desc' });
  
  // Record Payment Dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    driver_id: null,
    amount: "",
    payment_method: "cash",
    reference_number: "",
    notes: ""
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);
  
  // Detail dialog
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverReport, setDriverReport] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  
  // Collapsed sections
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [user?.id, cityFilter, commissionTypeFilter, hasDuesFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cityFilter && cityFilter !== "all") params.append("city_id", cityFilter);
      if (commissionTypeFilter && commissionTypeFilter !== "all") params.append("commission_type", commissionTypeFilter);
      if (hasDuesFilter === "yes") params.append("has_dues", "true");
      if (hasDuesFilter === "no") params.append("has_dues", "false");
      
      const cityParam = cityFilter !== "all" ? `?city_id=${cityFilter}` : "";
      
      const [reportRes, citiesRes, requestsRes, expiredRes, perRideRes, subRes] = await Promise.all([
        axios.get(`${API}/admin/${user.id}/commission-report?${params.toString()}`),
        axios.get(`${API}/cities?admin_id=${user.id}`),
        axios.get(`${API}/admin/${user.id}/commission-change-requests?status=pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/${user.id}/expired-subscriptions`).catch(() => ({ data: { expired: [], expiring_soon: [] } })),
        axios.get(`${API}/commission-reports/admin/${user.id}/per-ride${cityParam}`).catch(() => ({ data: null })),
        axios.get(`${API}/commission-reports/admin/${user.id}/subscription${cityParam}`).catch(() => ({ data: null }))
      ]);
      
      setReport(reportRes.data);
      // Sort cities alphabetically
      const sortedCities = (citiesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
      setChangeRequests(requestsRes.data || []);
      setExpiredSubscriptions(expiredRes.data);
      setPerRideReport(perRideRes.data);
      setSubscriptionReport(subRes.data);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load commission report");
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDetail = async (driverId) => {
    try {
      const res = await axios.get(`${API}/admin/${user.id}/driver/${driverId}/full-report`);
      setDriverReport(res.data);
      setShowDetailDialog(true);
    } catch (err) {
      toast.error("Failed to load driver details");
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await axios.put(`${API}/admin/commission-change-requests/${requestId}/approve`);
      toast.success("Commission change approved!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axios.put(`${API}/admin/commission-change-requests/${requestId}/reject`);
      toast.success("Commission change rejected");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
  };

  const handleRenewSubscription = async (driverId, days = 30) => {
    try {
      await axios.put(`${API}/admin/driver/${driverId}/commission/renew?days=${days}`);
      toast.success(`Subscription renewed for ${days} days`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to renew");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const filteredDrivers = (report?.drivers || [])
    .filter(d => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        d.driver_name?.toLowerCase().includes(search) ||
        d.driver_phone?.includes(search) ||
        d.city_name?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      const { key, direction } = sortConfig;
      let aVal = a[key];
      let bVal = b[key];
      
      if (key === 'driver_name' || key === 'city_name' || key === 'commission_type') {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      } else {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  
  // Sortable header component
  const SortableHeader = ({ label, sortKey }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50"
      onClick={() => setSortConfig(prev => ({
        key: sortKey,
        direction: prev.key === sortKey && prev.direction === 'asc' ? 'desc' : 'asc'
      }))}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === sortKey ? (
          sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
        ) : <ArrowUpDown className="w-4 h-4 opacity-30" />}
      </div>
    </TableHead>
  );
  
  // Open record payment dialog
  const openRecordPayment = (driver = null) => {
    setPaymentForm({
      driver_id: driver?.driver_id || null,
      amount: driver?.amount_owed?.toString() || "",
      payment_method: "cash",
      reference_number: "",
      notes: ""
    });
    setSelectedDriver(driver);
    setShowPaymentDialog(true);
  };
  
  // Handle payment submission
  const handleSubmitPayment = async () => {
    if (!paymentForm.driver_id || !paymentForm.amount) {
      toast.error("Please enter an amount");
      return;
    }
    
    setSubmittingPayment(true);
    try {
      await axios.post(`${API}/admin/payments`, {
        admin_id: user.id,
        payment_type: "driver_to_admin",
        recipient_id: paymentForm.driver_id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number || null,
        notes: paymentForm.notes || null
      });
      
      toast.success("Payment recorded successfully");
      setShowPaymentDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setSubmittingPayment(false);
    }
  };

  const exportReport = () => {
    const csv = [
      ["Driver Name", "Phone", "City", "Commission Type", "Total Rides", "Earnings", "Tips", "Commission", "Paid", "Dues"].join(","),
      ...filteredDrivers.map(d => [
        d.driver_name,
        d.driver_phone,
        d.city_name || "N/A",
        d.commission_type,
        d.total_rides,
        d.total_earnings,
        d.total_tips,
        d.total_commission,
        d.total_paid,
        d.amount_owed
      ].join(","))
    ].join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `commission-report-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commission-report-dashboard">
      {/* Pending Commission Change Requests */}
      {changeRequests.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-800 flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5" />
            Pending Commission Change Requests ({changeRequests.length})
          </h3>
          <div className="space-y-2">
            {changeRequests.map((req) => (
              <div key={req.id} className="flex items-center justify-between bg-white p-3 rounded border">
                <div>
                  <p className="font-medium">{req.driver_name} ({req.driver_phone})</p>
                  <p className="text-sm text-muted-foreground">
                    {req.current_type?.replace("_", " ")} → {req.requested_type?.replace("_", " ")}
                  </p>
                  {req.reason && <p className="text-sm text-gray-500">Reason: {req.reason}</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleApproveRequest(req.id)} data-testid={`approve-request-${req.id}`}>
                    <CheckCircle className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleRejectRequest(req.id)} data-testid={`reject-request-${req.id}`}>
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expired/Expiring Subscriptions Alert */}
      {(expiredSubscriptions?.expired?.length > 0 || expiredSubscriptions?.expiring_soon?.length > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5" />
            Subscription Alerts
          </h3>
          {expiredSubscriptions.expired?.length > 0 && (
            <div className="mb-3">
              <p className="text-sm font-medium text-red-700 mb-2">Expired ({expiredSubscriptions.expired.length})</p>
              <div className="space-y-2">
                {expiredSubscriptions.expired.slice(0, 5).map((d) => (
                  <div key={d.driver_id} className="flex items-center justify-between bg-white p-2 rounded border border-red-200">
                    <span className="text-sm">{d.driver_name} - {d.commission_type?.replace("_", " ")}</span>
                    <Button size="sm" variant="outline" onClick={() => handleRenewSubscription(d.driver_id, 30)}>
                      Renew 30 Days
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {expiredSubscriptions.expiring_soon?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-orange-700 mb-2">Expiring Soon ({expiredSubscriptions.expiring_soon.length})</p>
              <div className="space-y-2">
                {expiredSubscriptions.expiring_soon.slice(0, 5).map((d) => (
                  <div key={d.driver_id} className="flex items-center justify-between bg-white p-2 rounded border border-orange-200">
                    <span className="text-sm">{d.driver_name} - {d.days_until_expiry} days left</span>
                    <Button size="sm" variant="outline" onClick={() => handleRenewSubscription(d.driver_id, 30)}>
                      Extend
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="bg-card border rounded-lg p-4">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setSummaryExpanded(!summaryExpanded)}
        >
          <h3 className="font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Summary Overview
          </h3>
          {summaryExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
        
        {summaryExpanded && report?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-600">Total Drivers</p>
              <p className="text-2xl font-bold text-blue-700">{report.summary.total_drivers}</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-sm text-green-600">Total Earnings</p>
              <p className="text-2xl font-bold text-green-700">₹{report.summary.total_earnings?.toLocaleString()}</p>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg">
              <p className="text-sm text-yellow-600">Total Tips</p>
              <p className="text-2xl font-bold text-yellow-700">₹{report.summary.total_tips?.toLocaleString()}</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <p className="text-sm text-purple-600">Total Commission</p>
              <p className="text-2xl font-bold text-purple-700">₹{report.summary.total_commission?.toLocaleString()}</p>
            </div>
            <div className="bg-teal-50 p-3 rounded-lg">
              <p className="text-sm text-teal-600">Total Paid</p>
              <p className="text-2xl font-bold text-teal-700">₹{report.summary.total_paid?.toLocaleString()}</p>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <p className="text-sm text-red-600">Total Owed</p>
              <p className="text-2xl font-bold text-red-700">₹{report.summary.total_owed?.toLocaleString()}</p>
            </div>
          </div>
        )}
        
        {summaryExpanded && report?.summary?.drivers_by_commission_type && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-2">Drivers by Commission Type:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(report.summary.drivers_by_commission_type).map(([type, count]) => (
                <span key={type} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                  {type.replace("_", " ")}: <strong>{count}</strong>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report View Toggle */}
      <div className="flex gap-2 flex-wrap">
        <Button 
          variant={reportView === "combined" ? "default" : "outline"}
          onClick={() => setReportView("combined")}
          className="flex gap-2"
        >
          <Users className="w-4 h-4" />
          Combined View
        </Button>
        <Button 
          variant={reportView === "per-ride" ? "default" : "outline"}
          onClick={() => setReportView("per-ride")}
          className="flex gap-2"
        >
          <Percent className="w-4 h-4" />
          Per-Ride ({perRideReport?.summary?.total_drivers || 0})
        </Button>
        <Button 
          variant={reportView === "subscription" ? "default" : "outline"}
          onClick={() => setReportView("subscription")}
          className="flex gap-2"
        >
          <Calendar className="w-4 h-4" />
          Subscription ({subscriptionReport?.summary?.total_drivers || 0})
        </Button>
      </div>

      {/* Subscription Report View */}
      {reportView === "subscription" && subscriptionReport && (
        <div className="space-y-4">
          {/* Subscription Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <p className="text-sm text-blue-600">Daily</p>
              <p className="text-2xl font-bold text-blue-700">{subscriptionReport.summary.daily_drivers}</p>
              <p className="text-xs text-blue-500">₹{subscriptionReport.subscription_prices?.fixed_daily}/day</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <p className="text-sm text-green-600">Weekly</p>
              <p className="text-2xl font-bold text-green-700">{subscriptionReport.summary.weekly_drivers}</p>
              <p className="text-xs text-green-500">₹{subscriptionReport.subscription_prices?.fixed_weekly}/week</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <p className="text-sm text-purple-600">Monthly</p>
              <p className="text-2xl font-bold text-purple-700">{subscriptionReport.summary.monthly_drivers}</p>
              <p className="text-xs text-purple-500">₹{subscriptionReport.subscription_prices?.fixed_monthly}/month</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg text-center">
              <p className="text-sm text-amber-600">Total Dues</p>
              <p className="text-2xl font-bold text-amber-700">₹{subscriptionReport.summary.total_due_amount?.toLocaleString()}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <p className="text-sm text-red-600">Overdue</p>
              <p className="text-2xl font-bold text-red-700">{subscriptionReport.summary.overdue_renewals}</p>
              <p className="text-xs text-red-500">renewals</p>
            </div>
          </div>

          {/* Subscription Drivers Table */}
          <div className="bg-card border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">Subscription Drivers</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/commission-reports/admin/${user.id}/send-renewal-reminders?days_ahead=3`);
                    toast.success(`Sent ${res.data.sent} renewal reminders`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to send reminders");
                  }
                }}
                className="flex gap-2"
              >
                <Bell className="w-4 h-4" />
                Send Renewal Reminders
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>Next Renewal</TableHead>
                  <TableHead>Last Payment</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscriptionReport.drivers?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No subscription drivers found
                    </TableCell>
                  </TableRow>
                ) : (
                  subscriptionReport.drivers?.map((driver) => (
                    <TableRow key={driver.id} className={driver.is_renewal_overdue ? "bg-red-50" : ""}>
                      <TableCell>
                        <div>
                          <p className="font-medium flex items-center gap-1">
                            {driver.name}
                            {driver.is_online && <span className="w-2 h-2 rounded-full bg-green-500" />}
                          </p>
                          <p className="text-xs text-muted-foreground">{driver.phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm">
                          {driver.plan_name}
                        </span>
                      </TableCell>
                      <TableCell>₹{driver.subscription_amount}</TableCell>
                      <TableCell className={driver.due_amount > 0 ? "text-amber-600 font-medium" : ""}>
                        ₹{driver.due_amount}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(driver.plan_start_date)}</TableCell>
                      <TableCell className={`text-sm ${driver.is_renewal_overdue ? "text-red-600 font-medium" : ""}`}>
                        {formatDate(driver.next_renewal_date)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {driver.last_payment_date ? (
                          <>
                            {formatDate(driver.last_payment_date)}
                            <span className="text-xs text-muted-foreground block">₹{driver.last_payment_amount}</span>
                          </>
                        ) : "-"}
                      </TableCell>
                      <TableCell>
                        {driver.is_renewal_overdue ? (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs flex items-center gap-1 w-fit">
                            <AlertTriangle className="w-3 h-3" /> Overdue
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs flex items-center gap-1 w-fit">
                            <CheckCircle className="w-3 h-3" /> Active
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Per-Ride Report View */}
      {reportView === "per-ride" && perRideReport && (
        <div className="space-y-4">
          {/* Per-Ride Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <p className="text-sm text-blue-600">Total Drivers</p>
              <p className="text-2xl font-bold text-blue-700">{perRideReport.summary.total_drivers}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <p className="text-sm text-purple-600">Total Commission</p>
              <p className="text-2xl font-bold text-purple-700">₹{perRideReport.summary.total_commission?.toLocaleString()}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <p className="text-sm text-green-600">Collected</p>
              <p className="text-2xl font-bold text-green-700">₹{perRideReport.summary.total_collected?.toLocaleString()}</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg text-center">
              <p className="text-sm text-amber-600">Pending</p>
              <p className="text-2xl font-bold text-amber-700">₹{perRideReport.summary.total_pending?.toLocaleString()}</p>
              <p className="text-xs text-amber-500">{perRideReport.summary.drivers_with_dues} with dues</p>
            </div>
          </div>

          {/* Per-Ride Drivers Table */}
          <div className="bg-card border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Rides</TableHead>
                  <TableHead>Total Fare</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Pending</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perRideReport.drivers?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No per-ride drivers found
                    </TableCell>
                  </TableRow>
                ) : (
                  perRideReport.drivers?.map((driver) => (
                    <TableRow key={driver.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium flex items-center gap-1">
                            {driver.name}
                            {driver.is_online && <span className="w-2 h-2 rounded-full bg-green-500" />}
                          </p>
                          <p className="text-xs text-muted-foreground">{driver.phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-sm ${
                          driver.commission_type === "percentage" 
                            ? "bg-blue-100 text-blue-700" 
                            : "bg-orange-100 text-orange-700"
                        }`}>
                          {driver.commission_type === "percentage" ? "%" : "Flat"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {driver.commission_type === "percentage" 
                          ? `${driver.commission_rate}%` 
                          : `₹${driver.commission_rate}/ride`}
                      </TableCell>
                      <TableCell>{driver.total_rides}</TableCell>
                      <TableCell>₹{driver.total_fare?.toLocaleString()}</TableCell>
                      <TableCell>₹{driver.total_commission?.toLocaleString()}</TableCell>
                      <TableCell className="text-green-600">₹{driver.total_paid?.toLocaleString()}</TableCell>
                      <TableCell className={driver.pending_amount > 0 ? "text-amber-600 font-medium" : ""}>
                        ₹{driver.pending_amount?.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Combined View - Filters (only show in combined view) */}
      {reportView === "combined" && (
        <>
          {/* Filters */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <Label className="text-sm">City</Label>
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger data-testid="city-filter">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Commission Type</Label>
            <Select value={commissionTypeFilter} onValueChange={setCommissionTypeFilter}>
              <SelectTrigger data-testid="commission-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                {COMMISSION_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Has Dues</Label>
            <Select value={hasDuesFilter} onValueChange={setHasDuesFilter}>
              <SelectTrigger data-testid="dues-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">With Dues</SelectItem>
                <SelectItem value="no">No Dues</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Name or phone..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button variant="default" onClick={() => openRecordPayment(null)} data-testid="record-payment-btn">
              <Plus className="w-4 h-4 mr-2" /> Record Payment
            </Button>
            <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button variant="outline" onClick={exportReport} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
          </div>
        </div>
      </div>

      {/* Drivers Table */}
      <div className="bg-card border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader label="Driver" sortKey="driver_name" />
              <SortableHeader label="City" sortKey="city_name" />
              <SortableHeader label="Commission Type" sortKey="commission_type" />
              <SortableHeader label="Rides" sortKey="total_rides" />
              <SortableHeader label="Earnings" sortKey="total_earnings" />
              <SortableHeader label="Tips" sortKey="total_tips" />
              <SortableHeader label="Commission" sortKey="total_commission" />
              <SortableHeader label="Paid" sortKey="total_paid" />
              <SortableHeader label="Dues" sortKey="amount_owed" />
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDrivers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                  No drivers found matching filters
                </TableCell>
              </TableRow>
            ) : (
              filteredDrivers.map((driver) => (
                <TableRow key={driver.driver_id} data-testid={`driver-row-${driver.driver_id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{driver.driver_name}</p>
                      <p className="text-sm text-muted-foreground">{driver.driver_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell>{driver.city_name || "-"}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      driver.commission_type === "percentage" ? "bg-blue-100 text-blue-700" :
                      driver.commission_type === "flat_per_ride" ? "bg-green-100 text-green-700" :
                      "bg-purple-100 text-purple-700"
                    }`}>
                      {driver.commission_type?.replace("_", " ")}
                    </span>
                    {driver.has_override && (
                      <span className="ml-1 text-xs text-orange-600">(custom)</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{driver.total_rides}</TableCell>
                  <TableCell className="text-right">₹{driver.total_earnings?.toLocaleString()}</TableCell>
                  <TableCell className="text-right">₹{driver.total_tips?.toLocaleString()}</TableCell>
                  <TableCell className="text-right">₹{driver.total_commission?.toLocaleString()}</TableCell>
                  <TableCell className="text-right text-green-600">₹{driver.total_paid?.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <span className={driver.amount_owed > 0 ? "text-red-600 font-semibold" : "text-green-600"}>
                      ₹{driver.amount_owed?.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => fetchDriverDetail(driver.driver_id)}
                        data-testid={`view-detail-${driver.driver_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {driver.commission_history_count > 0 && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => {
                            setSelectedDriver(driver);
                            fetchDriverDetail(driver.driver_id);
                            setShowHistoryDialog(true);
                          }}
                          data-testid={`view-history-${driver.driver_id}`}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                      )}
                      {driver.amount_owed > 0 && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openRecordPayment(driver)}
                          data-testid={`collect-payment-${driver.driver_id}`}
                        >
                          Collect
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      </>
      )}

      {/* Driver Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Driver Report: {driverReport?.driver_name}</DialogTitle>
          </DialogHeader>
          {driverReport && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{driverReport.driver_phone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">City</p>
                  <p className="font-medium">{driverReport.city_name || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Vehicle</p>
                  <p className="font-medium">{driverReport.vehicle_type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Commission Type</p>
                  <p className="font-medium">{driverReport.commission?.type?.replace("_", " ")}</p>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-sm text-blue-600">Total Rides</p>
                  <p className="text-xl font-bold text-blue-700">{driverReport.summary?.total_rides}</p>
                </div>
                <div className="bg-green-50 p-3 rounded">
                  <p className="text-sm text-green-600">Total Earnings</p>
                  <p className="text-xl font-bold text-green-700">₹{driverReport.summary?.total_earnings?.toLocaleString()}</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded">
                  <p className="text-sm text-yellow-600">Total Tips</p>
                  <p className="text-xl font-bold text-yellow-700">₹{driverReport.summary?.total_tips?.toLocaleString()}</p>
                </div>
                <div className="bg-red-50 p-3 rounded">
                  <p className="text-sm text-red-600">Dues</p>
                  <p className="text-xl font-bold text-red-700">₹{driverReport.commission_summary?.amount_owed?.toLocaleString()}</p>
                </div>
              </div>

              {/* Commission Summary */}
              <div className="border rounded p-4">
                <h4 className="font-semibold mb-3">Commission Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Total Commission</p>
                    <p className="font-medium">₹{driverReport.commission_summary?.total_commission?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total Paid</p>
                    <p className="font-medium text-green-600">₹{driverReport.commission_summary?.total_paid?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Carried Over Dues</p>
                    <p className="font-medium">₹{driverReport.commission_summary?.carried_over_dues?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Amount Owed</p>
                    <p className="font-medium text-red-600">₹{driverReport.commission_summary?.amount_owed?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Breakdown by Booking Type */}
              {driverReport.breakdown?.by_booking_type && Object.keys(driverReport.breakdown.by_booking_type).length > 0 && (
                <div className="border rounded p-4">
                  <h4 className="font-semibold mb-3">By Booking Type</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(driverReport.breakdown.by_booking_type).map(([type, data]) => (
                      <div key={type} className="bg-gray-50 p-2 rounded">
                        <p className="text-sm font-medium capitalize">{type.replace("_", " ")}</p>
                        <p className="text-xs text-muted-foreground">{data.rides} rides • ₹{data.earnings}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Commission History */}
              {driverReport.commission?.history?.length > 0 && (
                <div className="border rounded p-4">
                  <h4 className="font-semibold mb-3">Commission History</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {driverReport.commission.history.map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm border-b pb-2">
                        <div>
                          <span className="font-medium">{entry.old_type?.replace("_", " ")} → {entry.new_type?.replace("_", " ")}</span>
                          {entry.carried_over_dues > 0 && (
                            <span className="ml-2 text-orange-600">(carried ₹{entry.carried_over_dues})</span>
                          )}
                        </div>
                        <span className="text-muted-foreground">
                          {new Date(entry.changed_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Payments */}
              {driverReport.recent_payments?.length > 0 && (
                <div className="border rounded p-4">
                  <h4 className="font-semibold mb-3">Recent Payments</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {driverReport.recent_payments.slice(0, 10).map((payment, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm border-b pb-2">
                        <div>
                          <span className="font-medium">₹{payment.amount}</span>
                          <span className="ml-2 text-muted-foreground">{payment.payment_method}</span>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          payment.status === "approved" ? "bg-green-100 text-green-700" :
                          payment.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                          "bg-gray-100 text-gray-700"
                        }`}>
                          {payment.status || "approved"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedDriver && (
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium">{selectedDriver.driver_name}</p>
                <p className="text-sm text-muted-foreground">{selectedDriver.driver_phone}</p>
                <p className="text-sm text-amber-600 mt-1">
                  Pending: ₹{(selectedDriver.amount_owed || 0).toLocaleString()}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(v) => setPaymentForm(prev => ({ ...prev, payment_method: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reference Number (Optional)</Label>
              <Input
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, reference_number: e.target.value }))}
                placeholder="Transaction ID or reference"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitPayment} disabled={submittingPayment}>
              {submittingPayment ? "Recording..." : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionReportDashboard;
